import React from 'react';
import { AlertTriangle, ArrowRight, BellRing, Plus } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  CRITICALITY_BANDS,
  formatChainPath,
  getPathRolesFromLevels,
  MAX_ESCALATION_LEVELS,
} from '../../constants/escalationMatrix.constants';
import type { EscalationContact } from '../../types/escalationContact.types';
import type {
  MatrixChainFormState,
  MatrixLevelFieldErrors,
  MatrixLevelFormState,
  MatrixValidationError,
} from '../../types/escalationMatrix.types';
import {
  parseTimerInput,
  chainHasZeroTimerError,
  CHAIN_ZERO_TIMER_MESSAGE,
} from '../../utils/escalationMatrixValidation';
import { addChainLevel, removeChainLevel } from '../../utils/escalationMatrixFormUtils';
import EscalationLevelBox from './EscalationLevelBox';

interface EscalationChainCardProps {
  chain: MatrixChainFormState;
  contacts: EscalationContact[];
  errors: MatrixValidationError[];
  fieldErrorsByLevel: Record<number, MatrixLevelFieldErrors>;
  showValidation: boolean;
  disabled?: boolean;
  onChange: (chain: MatrixChainFormState) => void;
}

export default function EscalationChainCard({
  chain,
  contacts,
  errors,
  fieldErrorsByLevel,
  showValidation,
  disabled = false,
  onChange,
}: Readonly<EscalationChainCardProps>) {
  const bandMeta = CRITICALITY_BANDS.find(item => item.value === chain.criticalityBand)!;
  const pathSummary = formatChainPath(getPathRolesFromLevels(chain.levels));
  const reminderError = showValidation
    ? errors.find(error => error.field === 'reminderIntervalMinutes')?.message
    : undefined;
  const zeroTimerError = chainHasZeroTimerError(chain.levels);

  const updateLevel = (clientId: string, patch: Partial<MatrixLevelFormState>) => {
    let levels = chain.levels.map(level =>
      level.clientId === clientId ? { ...level, ...patch } : level
    );

    if (patch.contactId != null) {
      levels = levels.map(level =>
        level.clientId !== clientId && level.contactId === patch.contactId
          ? { ...level, contactId: null }
          : level
      );
    }

    onChange({ ...chain, levels });
  };

  const getReservedContactIds = (currentClientId: string) =>
    new Set(
      chain.levels
        .filter(level => level.clientId !== currentClientId && level.contactId != null)
        .map(level => level.contactId as number)
    );

  return (
    <article className="esc-matrix-chain-card">
      <header className="esc-matrix-chain-header">
        <div className="esc-matrix-chain-heading">
          <span className={`esc-criticality-badge ${bandMeta.badgeClass}`}>{bandMeta.label}</span>
          <div>
            <div className="esc-matrix-chain-title-row">
              <h3 className="esc-matrix-chain-title">{bandMeta.chainTitle}</h3>
            </div>
            <p className="esc-matrix-chain-path">Path: {pathSummary}</p>
          </div>
        </div>

        <div className="esc-matrix-chain-settings">
          <label className="esc-matrix-reminder">
            <BellRing
              size={14}
              strokeWidth={ICON_STROKE_WIDTH}
              className="esc-matrix-reminder-icon"
              aria-hidden
            />
            <span className="esc-matrix-reminder-label">Remind every</span>
            <input
              type="text"
              inputMode="numeric"
              className={['esc-matrix-reminder-input', reminderError ? 'esc-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={chain.reminderIntervalMinutes ? String(chain.reminderIntervalMinutes) : ''}
              disabled={disabled}
              onChange={e =>
                onChange({ ...chain, reminderIntervalMinutes: parseTimerInput(e.target.value) })
              }
            />
            <span className="esc-matrix-reminder-suffix">min</span>
          </label>
        </div>
      </header>

      {reminderError && (
        <p
          className="esc-matrix-chain-error esc-matrix-chain-error--inline"
          role="alert"
        >
          {reminderError}
        </p>
      )}

      <div className="esc-matrix-flow">
        {chain.levels.map((level, index) => (
          <React.Fragment key={level.clientId}>
            <EscalationLevelBox
              level={level}
              contacts={contacts}
              reservedContactIds={getReservedContactIds(level.clientId)}
              fieldErrors={showValidation ? fieldErrorsByLevel[level.levelNumber] : undefined}
              canRemove={chain.levels.length > 1 && level.levelNumber !== 1}
              canMoveLeft={index > 1}
              canMoveRight={index < chain.levels.length - 1 && level.levelNumber !== 1}
              disabled={disabled}
              onChange={patch => updateLevel(level.clientId, patch)}
              onRemove={() => onChange(removeChainLevel(chain, level.clientId))}
              onMoveLeft={() => {
                const next = [...chain.levels];
                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                onChange({
                  ...chain,
                  levels: next.map((item, i) => ({
                    ...item,
                    levelNumber: i + 1,
                    orderIndex: i + 1,
                  })),
                });
              }}
              onMoveRight={() => {
                const next = [...chain.levels];
                [next[index], next[index + 1]] = [next[index + 1], next[index]];
                onChange({
                  ...chain,
                  levels: next.map((item, i) => ({
                    ...item,
                    levelNumber: i + 1,
                    orderIndex: i + 1,
                  })),
                });
              }}
            />
            {index < chain.levels.length - 1 && (
              <div
                className="esc-matrix-arrow"
                aria-hidden="true"
              >
                <ArrowRight
                  size={20}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {zeroTimerError && (
        <p
          className="esc-matrix-chain-error esc-matrix-chain-error--summary"
          role="alert"
        >
          <AlertTriangle
            size={14}
            strokeWidth={ICON_STROKE_WIDTH}
            aria-hidden
          />
          <span>{CHAIN_ZERO_TIMER_MESSAGE}</span>
        </p>
      )}

      {chain.levels.length < MAX_ESCALATION_LEVELS && (
        <div className="esc-matrix-chain-footer">
          <button
            type="button"
            className="esc-btn esc-btn--secondary esc-btn--inline"
            disabled={disabled}
            onClick={() => onChange(addChainLevel(chain))}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add level
          </button>
        </div>
      )}
    </article>
  );
}
